import {InlineAutocomplete} from '@github-ui/inline-autocomplete'
import {testIdProps} from '@github-ui/test-id-props'
import {ProjectTemplateIcon, XIcon} from '@primer/octicons-react'
import {ActionList, Box, CounterLabel, FormControl, Heading, Text, TextInput, Truncate} from '@primer/react'
import {Dialog, type DialogProps} from '@primer/react/lib-esm/Dialog/Dialog'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {useCallback, useEffect, useMemo, useState} from 'react'

import {useOrganizationTemplates} from '../../../client/queries/org-templates'
import {apiApplyTemplate} from '../../api/memex/api-apply-template'
import {CustomTemplateSelect, TemplatesCancel, TemplatesCreate} from '../../api/stats/contracts'
import {chartLayoutsMap} from '../../components/insights/chart-layouts'
import {getInitialState} from '../../helpers/initial-state'
import {useThemedMediaUrl} from '../../helpers/media-urls'
import {toTitleCase} from '../../helpers/util'
import {getViewIcons} from '../../helpers/view-type'
import {getWorkflowIcon} from '../../helpers/workflow-utilities'
import {useEmojiAutocomplete} from '../../hooks/common/use-emoji-autocomplete'
import {usePostStats} from '../../hooks/common/use-post-stats'
import {useApiRequest} from '../../hooks/use-api-request'
import {type ApplyTemplateCallback, useTemplateSideSelection} from '../../hooks/use-template-side-selection'
import {useSingleDataRefresh} from '../../state-providers/data-refresh/use-single-data-refresh'
import {useProjectDetails} from '../../state-providers/memex/use-project-details'
import {useUpdateMemexTitle} from '../../state-providers/memex/use-update-memex-title'
import {useTemplateDialog} from '../../state-providers/template-dialog/use-template-dialog'
import {getColumnIcon} from '../column-detail-helpers'
import {AutocompleteSearch} from '../common/autocomplete-search'
import {infoContentMap} from './main-content-data'
import {GroupEnum, type SelectionOption, type SidePanelGroup} from './types'

const actionListGroupStyle: BetterSystemStyleObject = {
  '&:not(:last-child)': {
    mb: 3,
  },
}

const actionListItemStyle: BetterSystemStyleObject = {
  position: 'relative',
  mb: 2,
  ml: '12px',
  '&.selected': {
    bg: 'neutral.muted',
  },
  '&.selected::after': {
    content: '""',
    position: 'absolute',
    top: 'calc(50% - 12px)',
    left: '-8px',
    width: '4px',
    height: '24px',
    bg: 'accent.emphasis',
    borderRadius: '6px',
  },
}

const templateInfoBoxStyle: BetterSystemStyleObject = {
  bg: 'canvas.inset',
  pt: 2,
  px: 3,
  pb: 3,
  borderRadius: '4px',
}

const templateInfoListStyle: BetterSystemStyleObject = {
  display: 'flex',
  flexWrap: 'wrap',
  pl: 0,
  listStyle: 'none',
  ml: '-8px',
  clipPath: 'inset(0 0 0 1px)',
}

const templateInfoListItemStyle: BetterSystemStyleObject = {
  py: 0,
  px: 2,
  borderLeftWidth: '1px',
  borderLeftStyle: 'solid',
  borderLeftColor: 'border.default',
}

const templatDialogBodyStyle: BetterSystemStyleObject = {
  display: 'flex',
  flexDirection: 'row',
  overflow: 'auto',
}

const templatDialogFooterStyle: BetterSystemStyleObject = {
  display: 'flex',
  justifyContent: 'flex-end',
  p: 3,
  pr: 4,
  boxShadow: theme => `0 -1px 0 ${theme.colors.border.default}`,
  'button > *': {
    pointerEvents: 'none',
  },
}

const orgTemplatesHeadingStyle: BetterSystemStyleObject = {
  color: 'fg.muted',
  fontSize: 0,
  fontWeight: 'bold',
  py: '6px',
  px: '16px',
}

const selectionPanelParentStyle: BetterSystemStyleObject = {
  minWidth: '160px',
  whiteSpace: 'nowrap',
  width: '30%',
  boxShadow: theme => `1px 0 0 ${theme.colors.border.default}`,
}

const infoPanelParentStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  width: '70%',
  minHeight: '544px',
  px: 4,
  py: 2,
}

const TemplateDialogBody: React.FC<DialogProps & {children?: React.ReactNode}> = ({children}) => {
  return <Box sx={templatDialogBodyStyle}>{children}</Box>
}

const TemplateDialogFooter: React.FC<DialogProps> = ({footerButtons}) => (
  <Box sx={templatDialogFooterStyle}>
    <Dialog.Buttons buttons={footerButtons ?? []} />
  </Box>
)

function useIsPrimerHackRenderApplied() {
  /**
   * This is a hack to work around aprimer bug, that will mount the AutoComplete overlay
   * before the dialog's overlay, making it impossible to interact with
   */
  const [isDialogRenderedAlready, setIsDialogRenderedAlready] = useState(false)
  useEffect(() => {
    // after the first render, then show that AutoComplete
    setIsDialogRenderedAlready(true)
  }, [])

  return isDialogRenderedAlready
}

export const TemplateDialog: React.FC = () => {
  const {isOrganization} = getInitialState()
  const {title} = useProjectDetails()
  const [currentTitle, setCurrentTitle] = useState(title)
  const sidePanelGroups = useTemplateSideSelection()
  const {postStats} = usePostStats()
  const {data = null} = useOrganizationTemplates({
    enabled: isOrganization,
  })

  const {isTemplatesDialogOpen, setIsTemplatesDialogOpen, showTemplateDialog, setIsModalDismissed} = useTemplateDialog()
  const [currentSelection, setCurrentSelection] = useState<SelectionOption>(sidePanelGroups[0].items[0])

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const memexDataRefresh = useSingleDataRefresh()

  const handleClick = async () => {
    // get item action
    const currentGroup = sidePanelGroups.find(group => group.key === currentSelection.groupKey)
    const currentItem = currentGroup?.items.find(item => item.key === currentSelection.key)

    if (currentItem === undefined) {
      return
    }

    setIsLoading(true)

    // apply template payload action
    await currentItem.action({
      selectedTemplate: currentSelection.key,
      rollback: () => setIsTemplatesDialogOpen(false),
      title: currentTitle,
    })

    // call for project refresh to then close the dialog
    await memexDataRefresh()

    setIsLoading(false)
    setIsTemplatesDialogOpen(false)
    // show omnibar discovery menu after dialog closes
    setIsModalDismissed(true)

    // hydro metrics
    if (currentSelection.groupKey === GroupEnum.org_templates) {
      postStats({
        name: TemplatesCreate,
        context: JSON.stringify({
          template: 'org_templates',
          template_project_title: currentSelection.template?.projectTitle,
          template_project_number: currentSelection.template?.projectNumber,
        }),
        ui: 'template_dialog',
      })
    } else {
      postStats({
        name: TemplatesCreate,
        context: JSON.stringify({template: currentSelection.key}),
        ui: 'template_dialog',
      })
    }
  }

  const handleClose = () => {
    postStats({name: TemplatesCancel})
    setIsTemplatesDialogOpen(false)
    setIsModalDismissed(true)
  }

  const handleSelect = (item: SelectionOption) => {
    setCurrentSelection(item)

    if (item.groupKey === GroupEnum.org_templates) {
      postStats({
        name: CustomTemplateSelect,
        context: JSON.stringify({
          template: 'org_templates',
          template_project_title: item.template?.projectTitle,
          template_project_number: item.template?.projectNumber,
        }),
        ui: 'template_dialog',
      })
    }
  }

  const templateImageUrl = useThemedMediaUrl('projectTemplateDialog', currentSelection.key)

  const autocompleteProps = useEmojiAutocomplete()
  const [selectedAutocomplete, setSelectedAutocomplete] = useState({text: '', id: '0'})
  const [isFilterApplied, setIsFilterApplied] = useState(false)
  const autocompleteTemplateList: Array<{text: string; id: string}> = useMemo(() => {
    if (data?.templates) {
      return data.templates.map(template => {
        return {
          text: template.projectTitle,
          id: `${template.projectNumber}`,
        }
      })
    }
    return []
  }, [data?.templates])

  const handleAutocompleteSelect = (selectedItem: {text: string; id: string}) => {
    if (selectedItem.id !== '0') {
      setIsFilterApplied(true)
      const currentGroup = sidePanelGroups.find(group => group.key === GroupEnum.org_templates)
      const select = currentGroup?.items.find(item => item.key === selectedItem.id.toString())
      if (select) {
        handleSelect(select)
      }
    } else {
      setIsFilterApplied(false)
    }

    setSelectedAutocomplete(selectedItem)
    return selectedItem
  }

  const isPrimerHackRenderApplied = useIsPrimerHackRenderApplied()

  if (!showTemplateDialog) {
    return null
  }

  function filterTemplates(selectedItemId: number) {
    return data?.templates.filter(function (template) {
      if (template.projectNumber === selectedItemId) {
        return template
      }
    })
  }

  type OrgTemplateProps = {selectedItemId: number}
  function OrgTemplates({selectedItemId}: OrgTemplateProps) {
    const filteredTemplates = filterTemplates(selectedItemId)
    const {updateTitle} = useUpdateMemexTitle()
    const applyTemplateRequest = useApiRequest({
      request: async (selectedTemplate: string) => {
        await apiApplyTemplate({template: selectedTemplate})
      },
      rollback: (rollbackAction?: () => void) => {
        if (rollbackAction) {
          rollbackAction()
        }
      },
      showErrorToast: true,
    })

    const handleApplyTemplate = useCallback<ApplyTemplateCallback>(
      async function (callbackArgs) {
        await Promise.all([
          applyTemplateRequest.perform(callbackArgs.selectedTemplate, callbackArgs.rollback),
          updateTitle(callbackArgs.title),
        ])
      },
      [applyTemplateRequest, updateTitle],
    )

    if (!isOrganization || !data) {
      return null
    }

    let templatesList = data.templates
    if (filteredTemplates && filteredTemplates.length > 0) {
      templatesList = filteredTemplates
    }

    const orgTemplatesGroup: SidePanelGroup = {
      key: GroupEnum.org_templates,
      groupTitle: 'Organization templates',
      items: templatesList.map(template => {
        return {
          key: template.projectNumber.toString(),
          groupKey: GroupEnum.org_templates,
          title: template.projectTitle,
          icon: ProjectTemplateIcon,
          template,
          action: handleApplyTemplate,
        } satisfies SelectionOption
      }),
    }

    sidePanelGroups.push(orgTemplatesGroup)

    const listLabelId = 'org-templates-list-heading'

    return (
      <>
        <Box id={listLabelId} sx={orgTemplatesHeadingStyle}>
          {orgTemplatesGroup.groupTitle}
        </Box>
        {isPrimerHackRenderApplied ? (
          <AutocompleteSearch
            items={autocompleteTemplateList}
            label="Search Templates"
            onSelect={handleAutocompleteSelect}
            selectedItem={selectedAutocomplete}
          />
        ) : null}

        <ActionList aria-labelledby={listLabelId} sx={{paddingTop: 0}}>
          <ActionList.Group
            key={orgTemplatesGroup.groupTitle}
            sx={{maxHeight: '300px', overflowY: 'auto', overflowX: 'hidden', ...actionListGroupStyle}}
          >
            {orgTemplatesGroup.items.map(item => {
              const selected =
                currentSelection.template &&
                item.template &&
                currentSelection.template.projectNumber === item.template.projectNumber
              return (
                <ActionList.Item
                  sx={actionListItemStyle}
                  className={selected ? 'selected' : ''}
                  onSelect={() => handleSelect(item)}
                  key={item.template?.projectNumber}
                >
                  <ActionList.LeadingVisual>
                    <ProjectTemplateIcon />
                  </ActionList.LeadingVisual>
                  <Truncate inline title={item.template?.projectTitle || ''} sx={{maxWidth: '100%'}}>
                    {item.template?.projectTitle}
                  </Truncate>
                </ActionList.Item>
              )
            })}
            {isFilterApplied ? (
              <ActionList.Item
                sx={{
                  color: 'accent.fg',
                }}
                onSelect={() => {
                  handleAutocompleteSelect({text: '', id: '0'})
                }}
              >
                <ActionList.LeadingVisual sx={{color: 'inherit'}}>
                  <XIcon />
                </ActionList.LeadingVisual>
                Clear Search
              </ActionList.Item>
            ) : null}
          </ActionList.Group>
        </ActionList>
      </>
    )
  }

  let infoTitle: string
  let infoDescription: string | undefined = undefined

  if (currentSelection.groupKey === GroupEnum.org_templates) {
    if (currentSelection.template) {
      infoTitle = currentSelection.template.projectTitle
    } else {
      infoTitle = '?'
    }
  } else {
    infoTitle = infoContentMap(currentSelection.key).title
    infoDescription = infoContentMap(currentSelection.key).description
  }

  return (
    <>
      {isTemplatesDialogOpen && (
        <Dialog
          role="alertdialog"
          onClose={handleClose}
          title="Select a template"
          renderBody={TemplateDialogBody}
          renderFooter={TemplateDialogFooter}
          sx={{
            width: ['100%', '960px'],
          }}
          footerButtons={[
            {
              content: (
                <span {...testIdProps('template-dialog-button')}>{isLoading ? 'Loading template...' : 'Create'}</span>
              ),
              onClick: handleClick,
              buttonType: 'primary',
              disabled: isLoading,
            },
          ]}
        >
          <Box sx={{display: 'flex'}}>
            <Box sx={selectionPanelParentStyle}>
              <ActionList>
                {sidePanelGroups.map(group => {
                  return (
                    <ActionList.Group sx={actionListGroupStyle} title={group.groupTitle} key={group.groupTitle}>
                      {group.items.map(item => {
                        const ItemIcon = item.icon
                        const selected = currentSelection.key === item.key
                        return (
                          <ActionList.Item
                            sx={actionListItemStyle}
                            className={selected ? 'selected' : ''}
                            disabled={item.disabled}
                            onSelect={() => handleSelect(item)}
                            key={item.key}
                            {...testIdProps(`template-dialog-action-item-${item.key}`)}
                          >
                            <ActionList.LeadingVisual>
                              <ItemIcon />
                            </ActionList.LeadingVisual>
                            {toTitleCase(item.title)}
                          </ActionList.Item>
                        )
                      })}
                    </ActionList.Group>
                  )
                })}
              </ActionList>
              <OrgTemplates selectedItemId={parseInt(selectedAutocomplete.id, 10)} />
            </Box>
            <Box sx={infoPanelParentStyle}>
              <Heading as="h2" sx={{mb: 2}}>
                {infoTitle}
              </Heading>
              <FormControl sx={{width: '100%'}}>
                <FormControl.Label>Project name</FormControl.Label>
                <InlineAutocomplete {...autocompleteProps} sx={{width: '100%'}}>
                  <TextInput
                    sx={{width: '100%'}}
                    autoComplete="off"
                    placeholder={infoTitle}
                    value={currentTitle}
                    onChange={e => setCurrentTitle(e.target.value)}
                    id="memexTitleInput"
                  />
                </InlineAutocomplete>
              </FormControl>
              {currentSelection.groupKey !== GroupEnum.org_templates && templateImageUrl && (
                <img
                  {...testIdProps('template-dialog-image')}
                  src={templateImageUrl}
                  alt={infoDescription}
                  width="100%"
                />
              )}
              {currentSelection.groupKey === GroupEnum.org_templates && currentSelection.template && (
                <>
                  {currentSelection.template.projectViews.length > 0 && (
                    <Box sx={templateInfoBoxStyle}>
                      <Text as="h4" sx={{width: '100%'}}>
                        Views
                        <CounterLabel sx={{marginLeft: '5px', verticalAlign: 'text-top'}}>
                          {currentSelection.template.projectViews.length}
                        </CounterLabel>
                      </Text>
                      <Box as="ul" sx={templateInfoListStyle}>
                        {currentSelection.template.projectViews.map(view => {
                          const ViewIcon = getViewIcons(view.viewType)
                          return (
                            <Box as="li" key={view.name} sx={templateInfoListItemStyle}>
                              {' '}
                              <ViewIcon /> {view.name}
                            </Box>
                          )
                        })}
                      </Box>
                    </Box>
                  )}
                  {currentSelection.template.projectFields.length > 0 && (
                    <Box sx={templateInfoBoxStyle}>
                      <Text as="h4" sx={{width: '100%'}}>
                        Fields
                        <CounterLabel sx={{marginLeft: '5px', verticalAlign: 'text-top'}}>
                          {currentSelection.template.projectFields.length}
                        </CounterLabel>
                      </Text>
                      <Box as="ul" sx={templateInfoListStyle}>
                        {currentSelection.template.projectFields.map(field => {
                          const FieldIcon = getColumnIcon(field.dataType)
                          return (
                            <Box as="li" sx={templateInfoListItemStyle} key={field.name}>
                              <FieldIcon /> {field.name}
                            </Box>
                          )
                        })}
                      </Box>
                    </Box>
                  )}
                  {currentSelection.template.projectWorkflows.length > 0 && (
                    <Box sx={templateInfoBoxStyle}>
                      <Text as="h4" sx={{width: '100%'}}>
                        Workflows
                        <CounterLabel sx={{marginLeft: '5px', verticalAlign: 'text-top'}}>
                          {currentSelection.template.projectWorkflows.length}
                        </CounterLabel>
                      </Text>
                      <Box as="ul" sx={templateInfoListStyle}>
                        {currentSelection.template.projectWorkflows.map(workflow => {
                          const WorkflowIcon = getWorkflowIcon(workflow.triggerType)
                          return (
                            <Box as="li" key={workflow.name} sx={templateInfoListItemStyle}>
                              <WorkflowIcon /> {workflow.name}
                            </Box>
                          )
                        })}
                      </Box>
                    </Box>
                  )}
                  {currentSelection.template.projectCharts.length > 0 && (
                    <Box sx={templateInfoBoxStyle}>
                      <Text as="h4" sx={{width: '100%'}}>
                        Insights
                        <CounterLabel sx={{marginLeft: '5px', verticalAlign: 'text-top'}}>
                          {currentSelection.template.projectCharts.length}
                        </CounterLabel>
                      </Text>
                      <Box as="ul" sx={templateInfoListStyle}>
                        {currentSelection.template.projectCharts.map(chart => {
                          const ChartIcon = chartLayoutsMap[chart.chartType].icon
                          return (
                            <Box as="li" key={chart.name} sx={templateInfoListItemStyle}>
                              <ChartIcon /> {chart.name}
                            </Box>
                          )
                        })}
                      </Box>
                    </Box>
                  )}
                </>
              )}
              {infoDescription && (
                <Text as="p" sx={{mt: 2}}>
                  {infoDescription}
                </Text>
              )}
            </Box>
          </Box>
        </Dialog>
      )}
    </>
  )
}

try{ TemplateDialogBody.displayName ||= 'TemplateDialogBody' } catch {}
try{ TemplateDialogFooter.displayName ||= 'TemplateDialogFooter' } catch {}
try{ TemplateDialog.displayName ||= 'TemplateDialog' } catch {}
try{ OrgTemplates.displayName ||= 'OrgTemplates' } catch {}
try{ ViewIcon.displayName ||= 'ViewIcon' } catch {}
try{ FieldIcon.displayName ||= 'FieldIcon' } catch {}
try{ WorkflowIcon.displayName ||= 'WorkflowIcon' } catch {}